import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, LifeBuoy, Mail, Search, X } from 'lucide-react';
import { useSiteTheme } from '../hooks/useSiteTheme';
import { usePageScroll } from '../hooks/usePageScroll';
import { LEGAL } from '../lib/legal';
import { HELP_TOPICS } from '../components/legal/helpContent';
import { SiteHeader } from '../components/billing/SiteHeader';
import { SiteFooter } from '../components/legal/SiteFooter';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

/** Plain text of a React node, for the search filter. */
const textOf = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(' ');
  if (node.props) return textOf(node.props.children);
  return '';
};

/**
 * /help — the Help Center: short answers grouped by topic (billing, referrals, Publisher import, your data, account),
 * with a search box that filters questions and answers, topic jump links and a stable anchor per question
 * (/help#billing-cancel). Readable signed in or out.
 */
export default function Help() {
  const [theme, toggleTheme] = useSiteTheme();
  usePageScroll(); // index.css hides body overflow for the editor; this page scrolls like a normal web page
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState([]);

  // deep link: /help#topic-question opens that answer and scrolls to it
  useEffect(() => {
    document.title = `Help Center — ${LEGAL.brand}`;
    const id = location.hash.replace('#', '');
    if (id && id.includes('-')) {
      setOpen((cur) => (cur.includes(id) ? cur : [...cur, id]));
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }), 50);
    } else if (id) {
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }), 50);
    } else {
      window.scrollTo(0, 0);
    }
    return () => { document.title = LEGAL.brand; };
  }, [location.hash]);

  const q = query.trim().toLowerCase();
  const topics = useMemo(() => {
    if (!q) return HELP_TOPICS;
    return HELP_TOPICS.map((t) => ({ ...t, items: t.items.filter((it) => `${it.q} ${textOf(it.a)}`.toLowerCase().includes(q)) })).filter((t) => t.items.length);
  }, [q]);
  const total = topics.reduce((n, t) => n + t.items.length, 0);
  // while searching, show every match expanded
  const value = q ? topics.flatMap((t) => t.items.map((it) => `${t.id}-${it.id}`)) : open;

  return (
    <main className="min-h-[100dvh] flex flex-col bg-background text-foreground" data-testid="help-page" data-theme={theme}>
      <SiteHeader theme={theme} onToggleTheme={toggleTheme} testId="help-header" />
      <section className="flex-1 w-full max-w-[1040px] mx-auto px-4 sm:px-6 pb-12 pt-2 sm:pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="help-back"><ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />Back to {LEGAL.brand}</Link>
        <div className="mt-4 flex items-start gap-3">
          <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><LifeBuoy className="h-5 w-5" aria-hidden="true" /></span>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={heading} data-testid="help-title">Help Center</h1>
            <p className="mt-1 text-sm text-muted-foreground">Short answers about billing, referrals, Publisher files and your data. Can’t find it? <a href={`mailto:${LEGAL.contactEmail}`} className="underline underline-offset-2 hover:text-foreground" data-testid="help-contact-link">{LEGAL.contactEmail}</a></p>
          </div>
        </div>

        <div className="mt-6 relative max-w-[560px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the help center…" aria-label="Search the help center" className="h-11 pl-9 pr-10 text-sm bg-card" data-testid="help-search" />
          {query && <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8" onClick={() => setQuery('')} aria-label="Clear search" data-testid="help-search-clear"><X className="h-4 w-4" /></Button>}
        </div>
        {q && <p className="mt-2 text-xs text-muted-foreground" data-testid="help-search-count" aria-live="polite">{total ? `${total} answer${total === 1 ? '' : 's'} match “${query.trim()}”` : `Nothing matches “${query.trim()}” — try another word, or email us.`}</p>}

        {!q && (
          <nav aria-label="Topics" className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="help-topics">
            {HELP_TOPICS.map((t) => (
              <a key={t.id} href={`#${t.id}`} className="group rounded-xl border border-border bg-card p-3 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors duration-150" data-testid={`help-topic-${t.id}`}>
                <t.icon className="h-4 w-4 text-primary" aria-hidden="true" />
                <div className="mt-2 text-sm font-semibold leading-tight" style={heading}>{t.title}</div>
                <div className="mt-1 text-[11px] text-muted-foreground leading-snug">{t.items.length} answers</div>
              </a>
            ))}
          </nav>
        )}

        <div className="mt-8 space-y-10" data-testid="help-sections">
          {topics.length === 0 && (
            <div className="rounded-xl border border-border bg-card p-6 text-sm flex flex-wrap items-center gap-3" data-testid="help-empty">
              <Mail className="h-5 w-5 text-primary" aria-hidden="true" /><span className="flex-1">No answers match. Email <a href={`mailto:${LEGAL.contactEmail}`} className="text-primary underline underline-offset-2">{LEGAL.contactEmail}</a> and we will help.</span>
              <Button type="button" variant="outline" size="sm" onClick={() => setQuery('')} data-testid="help-empty-clear">Show all topics</Button>
            </div>
          )}
          {topics.map((t) => (
            <section key={t.id} id={t.id} className="scroll-mt-6" data-testid={`help-section-${t.id}`}>
              <div className="flex items-center gap-2.5">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><t.icon className="h-4 w-4" aria-hidden="true" /></span>
                <div>
                  <h2 className="text-xl font-semibold tracking-tight" style={heading}>{t.title}</h2>
                  <p className="text-xs text-muted-foreground">{t.blurb}</p>
                </div>
              </div>
              <Accordion type="multiple" value={value} onValueChange={q ? undefined : setOpen} className="mt-3 rounded-xl border border-border bg-card px-4">
                {t.items.map((it) => {
                  const id = `${t.id}-${it.id}`;
                  return (
                    <AccordionItem key={id} value={id} id={id} className="scroll-mt-6 border-border" data-testid={`help-item-${id}`}>
                      <AccordionTrigger className="text-left text-[15px] font-medium hover:no-underline py-3.5" data-testid={`help-q-${id}`}>{it.q}</AccordionTrigger>
                      <AccordionContent className="wp-legal wp-help pb-4" data-testid={`help-a-${id}`}>{it.a}</AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </section>
          ))}
        </div>

        <p className="mt-10 text-xs text-muted-foreground" data-testid="help-legal-links">Looking for the story behind {LEGAL.brand}? <Link to="/about" className="underline underline-offset-2 hover:text-foreground" data-testid="help-about-link">About {LEGAL.brand}</Link> · the small print? <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">Terms of Service</Link> · <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link></p>
      </section>
      <SiteFooter testId="help-footer" />
    </main>
  );
}
